import type {DeepPartial} from '@/global';
import type {RGBA} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';
import {getUniqueString} from '@Utils/string.util';
import type {ItemTransitionObject, PageTransitionsObject} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {
  DEFAULT_DIP_TO_COLOR,
  DEFAULT_SLIDE_DIRECTION,
  DEFAULT_TRANSITION_DURATION,
  ItemTransitionType,
  MAX_TRANSITION_DURATION,
  MIN_TRANSITION_DURATION,
  SlideTransitionDirection,
} from '@PosterWhiteboard/page/transitions/page-transitions.types';

function isItemTransitionType(value: unknown): value is ItemTransitionType {
  return typeof value === 'string' && (Object.values(ItemTransitionType) as string[]).includes(value);
}

function isSlideTransitionDirection(value: unknown): value is SlideTransitionDirection {
  return typeof value === 'string' && (Object.values(SlideTransitionDirection) as string[]).includes(value);
}

function isRgbaColor(value: unknown): value is RGBA {
  return Array.isArray(value) && value.length === 4 && value.every((channel) => typeof channel === 'number' && Number.isFinite(channel));
}

export function clampTransitionDuration(duration: number): number {
  return Math.min(MAX_TRANSITION_DURATION, Math.max(MIN_TRANSITION_DURATION, duration));
}

/**
 * The single validated `unknown → ItemTransitionObject` point: narrows loosely-typed input
 * (backend JSON, DeepPartial updates, UI edits merged over an existing entity) into the
 * discriminated union, filling per-type defaults. The variant object is constructed
 * explicitly — stray keys from a type switch (e.g. a slide's `direction` after changing to
 * fade) are discarded. Returns null when the input can't form a valid transition.
 */
export function createItemTransitionObject(candidate: DeepPartial<ItemTransitionObject>): ItemTransitionObject | null {
  const {uid, fromItemUid, toItemUid, type} = candidate;
  if (!uid || !fromItemUid || !toItemUid || !isItemTransitionType(type)) {
    return null;
  }

  const base = {
    uid,
    fromItemUid,
    toItemUid,
    duration: clampTransitionDuration(typeof candidate.duration === 'number' && Number.isFinite(candidate.duration) ? candidate.duration : DEFAULT_TRANSITION_DURATION),
  };

  switch (type) {
    case ItemTransitionType.FADE:
      return {...base, type};
    case ItemTransitionType.SLIDE:
    case ItemTransitionType.WIPE:
      return {...base, type, direction: isSlideTransitionDirection(candidate.direction) ? candidate.direction : DEFAULT_SLIDE_DIRECTION};
    case ItemTransitionType.DIP_TO_COLOR:
      return {...base, type, color: isRgbaColor(candidate.color) ? candidate.color : DEFAULT_DIP_TO_COLOR};
    default:
      return assertNever(type);
  }
}

/**
 * Remaps a serialized transitions collection for copy/duplicate/paste: every transition gets a
 * fresh uid and its endpoints follow the item uid remapping. A transition whose endpoint isn't
 * in `itemUidMap` keeps the old reference — `revalidate()` prunes it after the paste lands.
 */
export function remapPageTransitionsObjectUids(transitionsObject: PageTransitionsObject, itemUidMap: Map<string, string>): PageTransitionsObject {
  const transitionsHashMap: Record<string, ItemTransitionObject> = {};
  for (const transition of Object.values(transitionsObject.transitionsHashMap)) {
    const newUid = getUniqueString();
    transitionsHashMap[newUid] = {
      ...transition,
      uid: newUid,
      fromItemUid: itemUidMap.get(transition.fromItemUid) ?? transition.fromItemUid,
      toItemUid: itemUidMap.get(transition.toItemUid) ?? transition.toItemUid,
    };
  }
  // A duplicated/pasted page never inherits the source page's timeline selection.
  return {transitionsHashMap, selectedBoundaryId: ''};
}
